import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { defaultUsers, USER_ROLES, User } from '../data/models';

// Define the shape of the context value
interface AuthContextType {
  currentUser: User | null;
  token:string;
  loading: boolean;
  error: string;
  loginClient: (governmentIdOrBusinessName: string, upi: string) => boolean;
  loginTeam: (email: string, password: string) => boolean;
  logout: () => void;
  hasRole: (role: string) => boolean;
  isClient: boolean;
  isFieldCollector: boolean;
  isSupervisor: boolean;
}

// Create the authentication context with a default value
const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  token:'',
  loading: true,
  error: '',
  loginClient: () => false,
  loginTeam: () => false,
  logout: () => {},
  hasRole: () => false,
  isClient: false,
  isFieldCollector: false,
  isSupervisor: false,
});

// Props for the AuthProvider component
interface AuthProviderProps {
  children: ReactNode;
}

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [token, setToken] = useState<string>('');

  // Load user from localStorage on initialization
  useEffect(() => {
    const storedUser = localStorage.getItem('valuation_user');
    const storedToken = localStorage.getItem('valuation_token');
    if (storedUser) {
      try {
        const parsedUser: User = JSON.parse(storedUser);
        setCurrentUser(parsedUser);
      } catch (e) {
        console.error('Failed to parse stored user data:', e);
      }
    }
    if (storedToken) {
      try {
        const parsedToken: string = storedToken;
        setToken(parsedToken);
      } catch (e) {
        console.error('Failed to parse stored user data:', e);
      }
    }
    setLoading(false);
  }, []);

  // Login function - for client users
  const loginClient = (governmentIdOrBusinessName: string, upi: string): boolean => {
    setError('');
    // Check if there's a client user with the provided credentials
    const user = defaultUsers.find(
      (u) => u.role === USER_ROLES.CLIENT && 
      (u.governmentId === governmentIdOrBusinessName || u.businessName === governmentIdOrBusinessName)
    );

    if (user) {
      // For now, we're not validating the UPI since it's for demo purposes
      const userWithUpi: User = {
        ...user,
        // upi // Store the UPI they entered for reference
      };
      setCurrentUser(userWithUpi);
      localStorage.setItem('propvalue_user', JSON.stringify(userWithUpi));
      return true;
    } else {
      setError('Invalid credentials. Please check your Government ID/Business Name.');
      return false;
    }
  };

  // Login function for team members (field collectors and supervisors)
  const loginTeam = (email: string, password: string): boolean => {
    setError('');
    // Find a team member with the provided credentials
    const user = defaultUsers.find(
      (u) => (u.role === USER_ROLES.DATA_COLLECTOR || u.role === USER_ROLES.SUPERVISOR) && 
      u.email === email && 
      u.password === password
    );

    if (user) {
      setCurrentUser(user);
      localStorage.setItem('propvalue_user', JSON.stringify(user));
      return true;
    } else {
      setError('Invalid email or password. Please try again.');
      return false;
    }
  };

  // Logout function
  const logout = (): void => {
    setCurrentUser(null);
     localStorage.clear(); 
  };

  // Check if the user has a specific role
  const hasRole = (role: string): boolean => {
    return currentUser?.role === role;
  };

  // Values to be provided to the context
  const value: AuthContextType = {
    currentUser,
    loading,
    token,
    error,
    loginClient,
    loginTeam,
    logout,
    hasRole,
    isClient: currentUser?.role === USER_ROLES.CLIENT,
    isFieldCollector: currentUser?.role === USER_ROLES.DATA_COLLECTOR,
    isSupervisor: currentUser?.role === USER_ROLES.SUPERVISOR,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;